;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:slug\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/b/:token\",\"regex\":\"^/b/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/en/:slug\",\"regex\":\"^/en/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/en/guide/:slug\",\"regex\":\"^/en/guide/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/guide/:slug\",\"regex\":\"^/guide/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/ledigt-jobb/:slug\",\"regex\":\"^/ledigt-jobb/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/allmanna-villkor\"},{\"path\":\"/boka\"},{\"path\":\"/en\"},{\"path\":\"/en/about-us\"},{\"path\":\"/en/book\"},{\"path\":\"/en/contact-us\"},{\"path\":\"/en/delete-account\"},{\"path\":\"/en/guide\"},{\"path\":\"/en/links\"},{\"path\":\"/en/terms-of-service\"},{\"path\":\"/en/work-with-us\"},{\"path\":\"/guide\"},{\"path\":\"/integritetspolicy\"},{\"path\":\"/jobba-hos-oss\"},{\"path\":\"/kontakta-oss\"},{\"path\":\"/links\"},{\"path\":\"/om-oss\"},{\"path\":\"/radera-konto\"},{\"path\":\"/ta-bort-konto\"}],\"isrRoutes\":[\"/:slug\",\"/en/:slug\",\"/en/guide/:slug\",\"/guide/:slug\"]}";globalThis["_sentryNextJsVersion"] = "16.1.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs";

/**
 * Stack frames that belong to someone else's JavaScript: Google Tag Manager and
 * the tags it injects, plus anything running out of a browser extension. An
 * event whose whole stack sits in these files is a bug in a GTM tag or in a
 * visitor's extension — we can't fix it from this repo, and it drowns out our
 * own errors.
 */
const THIRD_PARTY_FRAME =
  /(gtm\.js|gtag\/js|analytics\.js|fbevents\.js|googletagmanager\.com|doubleclick\.net|googlesyndication\.com)|^(chrome|moz|safari(-web)?|chrome-untrusted)-extension:\/\//i;

/**
 * Script the browser put into the page itself. Chrome on iOS and the Google app
 * inject their own script, and its frames carry the page's URL as filename; our
 * HTML is a handful of lines, so a frame far down "the document" is theirs (the
 * "Maximum call stack size exceeded" at line 226 on every page, 108 events, only
 * ever from those two browsers). An `<anonymous>` frame is eval'd code, which
 * the site never runs.
 */
function isInjectedFrame(frame: { filename?: string; abs_path?: string; lineno?: number }): boolean {
  const file = frame.filename ?? frame.abs_path ?? "";
  if (file === "<anonymous>") return true;
  return !/\.m?js(\?|$)/.test(file) && (frame.lineno ?? 0) > 50;
}

function isEntirelyThirdParty(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.flatMap(
    (value) => value.stacktrace?.frames ?? []
  );
  if (!frames?.length) return false;
  return frames.every(
    (frame) =>
      THIRD_PARTY_FRAME.test(frame.filename ?? frame.abs_path ?? "") ||
      isInjectedFrame(frame)
  );
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  // Local `next dev` runs push compile errors and HMR noise into the same issue
  // stream as production. Keep Sentry to deployed environments only.
  enabled: process.env.NODE_ENV === "production",

  sendDefaultPii: true,

  tracesSampleRate: 0.1,

  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  enableLogs: true,

  integrations: [Sentry.replayIntegration()],

  // Third-party noise we can't fix: browser extensions, in-app webviews,
  // Google Maps loader network flakes, blocked ad scripts.
  ignoreErrors: [
    "Invalid call to runtime.sendMessage", // Chrome extensions
    "Object Not Found Matching Id", // Outlook/Office link scanner
    "$ is not defined", // injected scripts, site has no jQuery
    "jQuery is not defined", // same, spelled out
    "window.ethereum", // crypto-wallet extensions
    "__firefox__", // Firefox iOS webview
    "Java object is gone", // Android webview
    "DarkReader", // Dark Reader extension
    "googletag is not defined", // ad-blocked Google Publisher Tag
    "gtm is not defined", // ad-blocked / half-loaded GTM
    "eventTracker is not defined", // undefined symbol inside a GTM tag
    "Cannot assign to read only property 'open' of object '#<Window>'", // popup-blocker extensions
    "Failed to execute 'removeChild' on 'Node'", // page-translation extensions rewriting the DOM
    "ResizeObserver loop", // benign browser notification, not a crash
    /Could not load "(controls|onion|map|util|overlay|marker|common|geometry|places)"/, // Google Maps JS loader network failures
  ],
  denyUrls: [
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari(-web)?-extension:\/\//,
    /maps\.googleapis\.com/,
    /pagead2\.googlesyndication\.com/,
    /ad\.doubleclick\.net/,
    /googletagmanager\.com/,
    /otSDKStub\.js|cookielaw\.org/, // OneTrust, injected by an extension; the site doesn't load it
  ],

  beforeSend(event) {
    return isEntirelyThirdParty(event) ? null : event;
  },
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
